import { useCallback, type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import NewWindowIcon from '@/public/icons/approval/new-window.svg';
import {
  formatSaTravelExceedRate,
  getSaTravelDailyAllowanceNotice,
  getSaTravelItems,
  isSaTravelRateExceeded,
  SA_TRAVEL_EXPENSE_COLUMNS,
  saTravelText,
  type SaTravelHeader,
} from '@/utils/approval/saTravelExpense';
import { SaTravelExpenseRoot } from './styles';

const LEGACY_ICON_HREF = '#svg-icon-new-window';

/** 저장 HTML 은 레거시 문서와 같은 아이콘 마크업 — 조회 화면에서 포탈 아이콘으로 바꿈 */
export const LegacyLinkIcon = () => (
  <svg width="13" height="13" className="c-black">
    <use href={LEGACY_ICON_HREF} />
  </svg>
);

export const PortalLinkIcon = () => <NewWindowIcon width={13} height={13} />;

type TableProps = {
  header: SaTravelHeader;
  t: TFunction;
  linkIcon: ReactNode;
};

export const NewWindowLink = ({
  href,
  icon,
}: {
  href: string;
  icon: ReactNode;
}) => (
  <a href={href} target="_blank" rel="noreferrer">
    {icon}
  </a>
);

/** 레거시 #eas_travel_box 안쪽 마크업(칸 구성·SAP 키 동일) */
const SaTravelExpenseTables = ({ header, t, linkIcon }: TableProps) => {
  const items = getSaTravelItems(header);
  const notice = getSaTravelDailyAllowanceNotice(header);
  const text = (key: keyof SaTravelHeader) => saTravelText(header[key]);

  return (
    <>
      <table className="table table-orders table-bordered table-fixed mb-10">
        <colgroup>
          <col width="20%" />
          <col width="*" />
        </colgroup>
        <tbody>
          <tr>
            <th className="text-center">{t('일비수령 여부')}</th>
            <td id="Zuse">{notice ? t(notice) : ''}</td>
          </tr>
        </tbody>
      </table>

      <div className="w-100 mb-10">
        <table className="table table-orders table-bordered table-fixed no-margin">
          <colgroup>
            <col width="20%" />
            <col width="*" />
            <col width="20%" />
            <col width="*" />
            <col width="50px" />
          </colgroup>
          <tbody>
            <tr>
              <th className="text-center">{t('출장코드')}</th>
              <td id="Trvnr">{text('Trvnr')}</td>
              <th className="text-center">{t('출장국가')}</th>
              <td id="Land1Fr">{text('Land1Fr')}</td>
              <th className="text-center">{t('상세')}</th>
            </tr>
            <tr>
              <th className="text-center">{t('출장부서')}</th>
              <td id="KostlT">{text('KostlT')}</td>
              <th className="text-center">{t('출장자명')}</th>
              <td id="PersonNm">{text('PersonNm')}</td>
              <td id="UrlLink1" className="text-center" rowSpan={2}>
                <NewWindowLink href={text('Urllink1')} icon={linkIcon} />
              </td>
            </tr>
            <tr>
              <th className="text-center">{t('출장기간')}</th>
              <td id="Trvdat">{text('Trvdat')}</td>
              <th className="text-center">{t('출장목적')}</th>
              <td id="Trvtyp">{text('Trvtyp')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <table className="table table-orders table-bordered table-fixed mb-10 text-center">
        <thead>
          <tr>
            <th>{t('구분')}</th>
            {SA_TRAVEL_EXPENSE_COLUMNS.map((c) => (
              <th key={c.label}>{t(c.label)}</th>
            ))}
            <th>{t('총합계')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>{t('사전품의 예산')}</th>
            {SA_TRAVEL_EXPENSE_COLUMNS.map((c) => (
              <td key={c.budget} id={c.budget}>
                {text(c.budget)}
              </td>
            ))}
            {/* 레거시는 칸 id(ZEXP_SUM)로 SAP 키를 찾아 늘 비었음 — 실제 키는 ZexpSum */}
            <td id="ZEXP_SUM">{text('ZexpSum')}</td>
          </tr>
          <tr>
            <th>{t('실 사용액')}</th>
            {SA_TRAVEL_EXPENSE_COLUMNS.map((c) => (
              <td key={c.actual} id={c.actual}>
                {text(c.actual)}
              </td>
            ))}
            <td id="ZEXP_AC_SUM">{text('ZexpAcSum')}</td>
          </tr>
          <tr>
            <th>{t('초과 사용률')}</th>
            {SA_TRAVEL_EXPENSE_COLUMNS.map((c, index) => {
              const rate = formatSaTravelExceedRate(
                header[c.budget],
                header[c.actual],
                c.budget === 'ZpaySum',
              );
              return (
                <td
                  key={c.label}
                  id={
                    c.budget === 'ZpaySum' ? 'ExpRateZ' : `ExpRate0${index + 1}`
                  }
                  style={
                    isSaTravelRateExceeded(rate) ? { color: 'red' } : undefined
                  }
                >
                  {rate}
                </td>
              );
            })}
            <td />
          </tr>
        </tbody>
      </table>

      <div>
        <table className="table table-orders table-bordered table-fixed no-margin text-center">
          <colgroup>
            <col width="40px" />
            <col width="43px" />
            <col width="60px" />
            <col width="*" />
            <col width="50px" />
            <col width="80px" />
            <col width="80px" />
            <col width="*" />
            <col width="88px" />
            <col width="*" />
            <col width="*" />
            <col width="45px" />
          </colgroup>
          <thead>
            <tr>
              <th>{t('순번')}</th>
              <th>{t('전표유형')}</th>
              <th>{t('전기일')}</th>
              <th>{t('전표번호')}</th>
              <th>{t('통화')}</th>
              <th>{t('입력금액')}</th>
              <th>{t('원화금액')}</th>
              <th>{t('적요')}</th>
              <th>{t('사용일자')}</th>
              <th>{t('사용처')}</th>
              <th>{t('사용처주소')}</th>
              <th>{t('보기')}</th>
            </tr>
          </thead>
          <tbody id="trv_disc_list">
            {items.map((item, index) => (
              <tr key={`${item.Seq ?? ''}-${index}`}>
                <td>{item.Seq || ''}</td>
                <td>{item.Blart || ''}</td>
                <td>{item.Budat || ''}</td>
                <td>{item.Belnr || ''}</td>
                <td>{item.Waers || ''}</td>
                <td>{item.Wrbtr || ''}</td>
                <td>{item.Dmbtr || ''}</td>
                <td>{item.Bktxt || ''}</td>
                <td>{item.Text1 || ''}</td>
                <td>{item.Text2 || ''}</td>
                <td>{item.Text3 || ''}</td>
                <td>
                  <NewWindowLink href={item.Urllink2 || ''} icon={linkIcon} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={6}>{t('합계')}</th>
              <td id="Zsum">{items[0]?.Zsum || ''}</td>
              <th colSpan={5} />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SaTravelExpenseTableView = ({
  header,
  t,
}: Omit<TableProps, 'linkIcon'>) => (
  <SaTravelExpenseRoot>
    <SaTravelExpenseTables
      header={header}
      t={t}
      linkIcon={<PortalLinkIcon />}
    />
  </SaTravelExpenseRoot>
);

/** attributes.content — 레거시 저장 범위(#eas_travel_box 안쪽)와 같음 */
export const buildSaTravelExpenseContentHtml = (
  header: SaTravelHeader,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <SaTravelExpenseTables
      header={header}
      t={t}
      linkIcon={<LegacyLinkIcon />}
    />,
  );

const DROP_TAGS = new Set(['style', 'script', 'link']);
// 레거시 content 의 줄바꿈 공백이 표 태그 바로 아래 남으면 React 경고
const TABLE_PARTS = new Set([
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'colgroup',
]);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();

/** 저장 content(레거시 문서 포함) 그대로 — 177·179 공통 */
export const useSaTravelSnapshotNodes = (content: string) => {
  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      // 레거시 아이콘(svg 스프라이트·FontAwesome)이 포탈엔 없음
      if (
        (tagName === 'svg' &&
          element.querySelector(`use[href="${LEGACY_ICON_HREF}"]`)) ||
        (tagName === 'i' && element.classList.contains('fa-external-link'))
      )
        return <PortalLinkIcon key={props.key as string} />;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      // 레거시 인라인 flex·overflow 는 포탈 스크롤 래퍼로 대신함
      if (tagName === 'div' && props.style) {
        return (
          <div key={props.key as string} className={props.className as string}>
            {children}
          </div>
        );
      }
      return undefined;
    },
    [],
  );
  return useHtmlToReact(content, { transform });
};

export const SaTravelExpenseSnapshot = ({ content }: { content: string }) => (
  <SaTravelExpenseRoot>{useSaTravelSnapshotNodes(content)}</SaTravelExpenseRoot>
);
